/**
 * @module Modal
 */
import {ClientApplication} from '../../client';
import {isSafe} from '../../util/@shopify/safe-redirect';
import {getSingleButton} from '../buttonHelper';
import {
  actionWrapper,
  forEachInEnum,
  getMergedProps,
  updateActionFromPayload,
  ActionSetWithChildren,
} from '../helper';
import {ActionSetProps, ClickAction, ComponentType, Group, MetaAction} from '../types';
import {
  clickButton,
  getSafeButtonProps,
  isValidButtonProps,
  validationError as buttonValidationError,
  ActionType as ButtonActionType,
  Button,
  Payload as ButtonPayload,
} from '../Button';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
  Message as ErrorMessage,
} from '../Error';
import {isValidAbsolutePath, isValidRelativePath} from '../Navigation/helper';
import {
  Action,
  ActionType,
  ClosePayload,
  Footer,
  FooterActionType,
  FooterOptions,
  IframeOptions,
  IframePayload,
  MessageOptions,
  MessagePayload,
  Size,
} from './types';

export const GROUP = Group.Modal;

const FOOTER_BUTTON_PROPS = {
  group: GROUP,
  subgroups: ['Footer'],
  type: ComponentType.Button,
};

export interface ActionBase extends MetaAction {
  readonly group: typeof GROUP;
}

export interface OpenAction extends ActionBase {
  readonly type: typeof ActionType.OPEN;
  readonly payload: MessagePayload | IframePayload;
}

export type UpdateAction = OpenAction;

export interface CloseAction extends ActionBase {
  readonly type: typeof ActionType.CLOSE;
}

export type ModalAction = OpenAction | UpdateAction | CloseAction | MetaAction;

export function openModal(modalPayload: MessagePayload | IframePayload): OpenAction {
  return actionWrapper({
    group: GROUP,
    payload: modalPayload,
    type: ActionType.OPEN,
  });
}

export function closeModal(modalClosePayload: ClosePayload): CloseAction {
  return actionWrapper({
    group: GROUP,
    payload: modalClosePayload,
    type: ActionType.CLOSE,
  });
}

export function clickFooterButton(id: string, payload?: any): ClickAction {
  const component = {id, ...FOOTER_BUTTON_PROPS};

  return clickButton(GROUP, component, payload);
}

export function update(payload: MessagePayload | IframePayload): UpdateAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.UPDATE,
  });
}

export function isValidSrc(options: Partial<IframeOptions | IframePayload>, localOrigin?: string) {
  const {url, path} = options;
  if (!url && !path) {
    return false;
  }

  if (path && !isValidRelativePath(path)) {
    return false;
  }

  if (url) {
    if (!localOrigin) {
      return false;
    }
    if (!isValidAbsolutePath(url)) {
      return false;
    }

    let hostName;
    try {
      hostName = new URL(localOrigin).hostname;
    } catch (error) {
      return false;
    }

    const isSafeSrc = isSafe(url, {
      requireAbsolute: true,
      requireSSL: true,
      whitelist: [hostName],
    });

    if (!isSafeSrc) {
      return false;
    }
  }

  return true;
}

export function validationError(
  action: OpenAction | UpdateAction | CloseAction | ClickAction | MetaAction,
  localOrigin?: string,
): undefined | ErrorAction {
  let payload;
  switch (action.type) {
    case ActionType.OPEN:
      payload = (action as OpenAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }
      if (payload.id && typeof payload.id !== 'string') {
        return invalidPayload(action, ErrorMessage.INVALID_PAYLOAD_ID);
      }

      return isValidModalProps(payload, localOrigin) ? undefined : invalidPayload(action);

    case ActionType.UPDATE:
      payload = (action as UpdateAction).payload;

      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      if (typeof payload.id !== 'string') {
        return invalidPayload(action, ErrorMessage.INVALID_PAYLOAD_ID);
      }

      // Id is always required when updating
      return isValidModalProps(payload, localOrigin) ? undefined : invalidPayload(action);

    case ActionType.CLOSE:
      payload = (action as CloseAction).payload;

      if (payload && payload.id && typeof payload.id !== 'string') {
        return invalidPayload(action, ErrorMessage.INVALID_PAYLOAD_ID);
      }

      return undefined;

    case FooterActionType.FOOTER_BUTTON_CLICK:
      return buttonValidationError({...action, type: ButtonActionType.CLICK});

    case FooterActionType.FOOTER_BUTTON_UPDATE:
      return buttonValidationError({...action, type: ButtonActionType.UPDATE});

    default:
      return invalidActionType(action);
  }
}

export function isValidModalProps(
  props: (MessageOptions | IframeOptions) | (MessagePayload | IframePayload) | undefined,
  localOrigin?: string,
) {
  if (props === undefined) {
    return false;
  }

  if (
    isIframeModal(props) &&
    (typeof localOrigin !== 'string' || !isValidSrc(props, localOrigin))
  ) {
    return false;
  }

  return (
    (isMessageModal(props) || isIframeModal(props)) &&
    isValidModalFooter(props.footer) &&
    isValidModalSize(props.size)
  );
}

export function isValidModalFooter(footer?: Footer) {
  if (footer === undefined || !footer.hasOwnProperty('buttons')) {
    return true;
  }

  if (
    footer.hasOwnProperty('buttons') &&
    (Array.isArray(footer.buttons) || typeof footer.buttons !== 'object')
  ) {
    return false;
  }

  const {secondary, primary} = footer.buttons;

  if (primary && !isValidButtonProps(primary)) {
    return false;
  }

  if (secondary && !Array.isArray(secondary)) {
    return false;
  } else if (Array.isArray(secondary)) {
    for (const button of secondary) {
      if (!isValidButtonProps(button)) {
        return false;
      }
    }
  }

  return true;
}

export function isValidModalSize(size?: string) {
  if (size === undefined) {
    return true;
  }
  let valid = false;
  forEachInEnum(Size, sizeOption => {
    if (size === sizeOption) {
      valid = true;
    }
  });

  return valid;
}

export function isIframeModal(
  options: MessagePayload | IframePayload | object,
): options is IframePayload {
  return (
    typeof (options as IframePayload).url === 'string' ||
    typeof (options as IframePayload).path === 'string'
  );
}

export function isMessageModal(
  options: MessagePayload | IframePayload | object,
): options is MessagePayload {
  return typeof (options as MessagePayload).message === 'string';
}

export abstract class Modal extends ActionSetWithChildren {
  title?: string;
  size = Size.Small;
  footerPrimary?: ButtonPayload;
  footerPrimaryOptions?: Button;
  footerSecondary?: ButtonPayload[];
  footerSecondaryOptions?: Button[];

  get footer(): Footer | undefined {
    if (!this.footerPrimary && !this.footerSecondary) {
      return undefined;
    }

    return {
      buttons: {
        primary: this.footerPrimary,
        secondary: this.footerSecondary,
      },
    };
  }

  get footerOptions(): FooterOptions | undefined {
    if (!this.footerPrimaryOptions && !this.footerSecondaryOptions) {
      return undefined;
    }

    return {
      buttons: {
        primary: this.footerPrimaryOptions,
        secondary: this.footerSecondaryOptions,
      },
    };
  }

  protected close() {
    this.app.dispatch(closeModal({id: this.id}));
  }

  protected setFooterPrimaryButton(newOptions: Button | undefined, updateCb: () => void) {
    const {subgroups} = FOOTER_BUTTON_PROPS;
    this.footerPrimaryOptions = this.getChildButton(newOptions, this.footerPrimaryOptions);
    this.footerPrimary = this.footerPrimaryOptions
      ? getSingleButton(this, this.footerPrimaryOptions, subgroups, (newPayload: ButtonPayload) => {
          this.updatePrimaryFooterButton(newPayload, updateCb);
        })
      : undefined;
  }

  protected setFooterSecondaryButtons(newOptions: Button[] | undefined, updateCb: () => void) {
    const {subgroups} = FOOTER_BUTTON_PROPS;
    const newButtons = newOptions || [];
    const currentOptions = (this.footerOptions && this.footerOptions.buttons.secondary) || [];
    this.footerSecondaryOptions = this.getUpdatedChildActions(newButtons, currentOptions);

    this.footerSecondary = this.footerSecondaryOptions
      ? this.footerSecondaryOptions.map(action =>
          getSingleButton(this, action, subgroups, (newPayload: ButtonPayload) => {
            this.updateSecondaryFooterButton(newPayload, updateCb);
          }),
        )
      : undefined;
  }

  protected getChildButton(newAction: undefined | Button, currentAction: undefined | Button) {
    const newButtons = newAction ? [newAction] : [];
    const currentButtons = currentAction ? [currentAction] : [];
    const updatedButton = this.getUpdatedChildActions(newButtons, currentButtons);

    return updatedButton ? updatedButton[0] : undefined;
  }

  protected updatePrimaryFooterButton(newPayload: ButtonPayload, updateCb: () => void) {
    if (!this.footer || !this.footer.buttons.primary) {
      return;
    }
    if (updateActionFromPayload(this.footer.buttons.primary, getSafeButtonProps(newPayload))) {
      updateCb();
    }
  }

  protected updateSecondaryFooterButton(newPayload: ButtonPayload, updateCb: () => void) {
    if (!this.footer || !this.footer.buttons || !this.footer.buttons.secondary) {
      return;
    }
    let updated;
    for (const action of this.footer.buttons.secondary) {
      updated = updateActionFromPayload(action, getSafeButtonProps(newPayload));
      if (updated) {
        break;
      }
    }
    if (updated) {
      updateCb();
    }
  }

  protected validateOptions(options: MessageOptions | IframeOptions) {
    const props = {id: this.id, ...options};
    if (!isValidModalProps(options, this.app.localOrigin)) {
      const errorAction = update(props);
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);

      return false;
    }

    return true;
  }
}

export class ModalMessage extends Modal implements ActionSetProps<MessageOptions, MessagePayload> {
  message!: string;

  constructor(app: ClientApplication<any>, options: MessageOptions) {
    super(app, GROUP, GROUP);
    this.set(options, false);
  }

  get payload() {
    return {
      ...this.options,
      footer: this.footer,
      id: this.id,
    };
  }

  get options() {
    return {
      footer: this.footerOptions,
      message: this.message,
      size: this.size,
      title: this.title,
    };
  }

  set(options: Partial<MessageOptions>, shouldUpdate = true) {
    const mergedOptions = getMergedProps(this.options, options);
    const {title, footer, message, size} = mergedOptions;

    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.title = title;
    this.message = message;
    this.size = size;
    this.setFooterPrimaryButton(footer ? footer.buttons.primary : undefined, () => {
      this.dispatch(Action.UPDATE);
    });

    this.setFooterSecondaryButtons(footer ? footer.buttons.secondary : undefined, () => {
      this.dispatch(Action.UPDATE);
    });

    if (shouldUpdate) {
      this.dispatch(Action.UPDATE);
    }

    return this;
  }

  dispatch(action: Action) {
    switch (action) {
      case Action.OPEN:
        this.app.dispatch(openModal(this.payload));
        break;
      case Action.CLOSE:
        this.close();
        break;
      case Action.UPDATE:
        this.app.dispatch(update(this.payload));
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }
}

export class ModalIframe extends Modal implements ActionSetProps<IframeOptions, IframePayload> {
  url?: string;
  path?: string;

  constructor(app: ClientApplication<any>, options: IframeOptions) {
    super(app, GROUP, GROUP);
    this.set(options, false);
  }

  get payload() {
    return {
      ...this.options,
      footer: this.footer,
      id: this.id,
    };
  }

  get options() {
    return {
      footer: this.footerOptions,
      path: this.path,
      size: this.size,
      title: this.title,
      url: this.url,
    };
  }

  set(options: Partial<IframeOptions>, shouldUpdate = true) {
    const mergedOptions = getMergedProps(this.options, options);
    const {title, footer, path, url, size} = mergedOptions;

    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.title = title;
    this.url = url;
    this.path = path;
    this.size = size;

    this.setFooterPrimaryButton(footer ? footer.buttons.primary : undefined, () => {
      this.dispatch(Action.UPDATE);
    });

    this.setFooterSecondaryButtons(footer ? footer.buttons.secondary : undefined, () => {
      this.dispatch(Action.UPDATE);
    });

    if (shouldUpdate) {
      this.dispatch(Action.UPDATE);
    }

    return this;
  }

  dispatch(action: Action) {
    switch (action) {
      case Action.OPEN:
        this.app.dispatch(openModal(this.payload));
        break;
      case Action.CLOSE:
        this.close();
        break;
      case Action.UPDATE:
        this.app.dispatch(update(this.payload));
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }
}

export const create = (app: ClientApplication<any>, options: MessageOptions | IframeOptions) => {
  if (isIframeModal(options)) {
    return new ModalIframe(app, options);
  }

  return new ModalMessage(app, options);
};



// WEBPACK FOOTER //
// ./src/actions/Modal/actions.ts